import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

// Landing Page
import LandingPage from "@/pages/LandingPage";

// Auth Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

// Main App Pages
import DashboardPage from "@/pages/DashboardPage";
import CalendarPage from "@/pages/CalendarPage";
import ClientsPage from "@/pages/ClientsPage";
import AccountingPage from "@/pages/AccountingPage";
import SettingsPage from "@/pages/SettingsPage";
import QRCodePage from "@/pages/QRCodePage";
import PublicBookingPage from "@/pages/PublicBookingPage";
import BookingPaymentCallback from "@/pages/BookingPaymentCallback";
import SubscriptionPage from "@/pages/SubscriptionPage";
import SubscriptionCallback from "@/pages/SubscriptionCallback";
import InvoicesPage from "@/pages/InvoicesPage";
import AppointmentDetailPage from "@/pages/AppointmentDetailPage";
import ErrorPage from "@/pages/ErrorPage";
import Layout from "@/components/Layout";

// SuperAdmin imports
import SuperAdminRegisterPage from "@/pages/SuperAdminRegisterPage";
import SuperAdminLoginPage from "@/pages/SuperAdminLoginPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import SuperAdminManagePage from "@/pages/SuperAdminManagePage";
import SuperAdminSalonDetailPage from "@/pages/SuperAdminSalonDetailPage";
import SuperAdminTransactionsPage from "@/pages/SuperAdminTransactionsPage";
import SuperAdminActivityLogsPage from "@/pages/SuperAdminActivityLogsPage";

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }
    
    if (!user) {
        return <Navigate to="/login" replace />;
    }
    
    return children;
}

function PublicRoute({ children }) {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }
    
    if (user) {
        return <Navigate to="/dashboard" replace />;
    }
    
    return children;
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    {/* Landing Page - Home */}
                    <Route path="/" element={<LandingPage />} />
                    
                    {/* Public Booking Routes */}
                    <Route path="/book/:salonId" element={<PublicBookingPage />} />
                    <Route path="/booking/payment/callback" element={<BookingPaymentCallback />} />
                    <Route path="/booking/verify" element={<BookingPaymentCallback />} />
                    
                    {/* Subscription Callback */}
                    <Route path="/subscription/verify" element={
                        <ProtectedRoute>
                            <SubscriptionCallback />
                        </ProtectedRoute>
                    } />
                    <Route path="/subscription/callback" element={
                        <ProtectedRoute>
                            <SubscriptionCallback />
                        </ProtectedRoute>
                    } />
                    
                    {/* SuperAdmin Routes */}
                    <Route path="/superadmin/register" element={<SuperAdminRegisterPage />} />
                    <Route path="/superadmin/login" element={<SuperAdminLoginPage />} />
                    <Route path="/superadmin" element={<SuperAdminPage />} />
                    <Route path="/superadmin/admins" element={<SuperAdminManagePage />} />
                    <Route path="/superadmin/salons/:salonId" element={<SuperAdminSalonDetailPage />} />
                    <Route path="/superadmin/transactions" element={<SuperAdminTransactionsPage />} />
                    <Route path="/superadmin/logs" element={<SuperAdminActivityLogsPage />} />
                    
                    {/* Auth Routes */}
                    <Route path="/login" element={
                        <PublicRoute>
                            <LoginPage />
                        </PublicRoute>
                    } />
                    <Route path="/register" element={
                        <PublicRoute>
                            <RegisterPage />
                        </PublicRoute>
                    } />
                    <Route path="/forgot-password" element={
                        <PublicRoute>
                            <ForgotPasswordPage />
                        </PublicRoute>
                    } />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    
                    {/* Protected App Routes - Using Layout */}
                    <Route element={
                        <ProtectedRoute>
                            <Layout />
                        </ProtectedRoute>
                    }>
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/calendar" element={<CalendarPage />} />
                        <Route path="/appointments/:id" element={<AppointmentDetailPage />} />
                        <Route path="/clients" element={<ClientsPage />} />
                        <Route path="/accounting" element={<AccountingPage />} />
                        <Route path="/qrcode" element={<QRCodePage />} />
                        <Route path="/subscription" element={<SubscriptionPage />} />
                        <Route path="/invoices" element={<InvoicesPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                    </Route>
                    
                    {/* 404 Error Page - Catch all */}
                    <Route path="*" element={<ErrorPage />} />
                </Routes>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
        </AuthProvider>
    );
}

export default App;